import { yupBoolean, yupEnum, yupObject } from 'shared/utils/YupUtils';
import { InferType } from 'yup';
import { UserSettingsPhoneState } from './LoadboardUserSettingsDTO';

export type NotificationsSettingActivity = NotificationsSettingDTO['activity'];
export type NotificationsSettingDTO = InferType<
  typeof notificationsSettingSchema
>;
export const notificationsSettingSchema = yupObject({
  active: yupBoolean().default(true),
  email_enabled: yupBoolean(),
  push_enabled: yupBoolean(),
  sms_enabled: yupBoolean(),
  activity: yupEnum([
    'request_declined',
    'saved_search',
    'load_suggested',
  ]).defined(),
});

export type NotificationsSettingPayloadDTO = InferType<
  typeof notificationsSettingPayloadSchema
>;
export const notificationsSettingPayloadSchema =
  notificationsSettingSchema.shape({
    active: yupBoolean().default(undefined),
    email_enabled: yupBoolean().default(undefined),
    sms_enabled: yupBoolean().default(undefined),
    push_enabled: yupBoolean().default(undefined),
  });

export type NotificationSettingsValuesDTO = InferType<
  typeof notificationSettingsValuesSchema
>;
export const notificationSettingsValuesSchema = yupObject({
  request_declined: yupObject({
    email_enabled: yupBoolean(),
    push_enabled: yupBoolean(),
  }),
  saved_search: yupObject({
    sms_enabled: yupBoolean(),
    email_enabled: yupBoolean(),
    push_enabled: yupBoolean(),
  }),
  load_suggested: yupObject({
    sms_enabled: yupBoolean(),
    email_enabled: yupBoolean(),
  })
    .nullable()
    .default(null),
});

export function mapPayloadToValues(settings: NotificationsSettingDTO[]) {
  const value: Record<string, unknown> = {};

  for (const setting of settings) {
    value[setting.activity] = {
      email_enabled: setting.email_enabled,
      sms_enabled: setting.sms_enabled,
      push_enabled: setting.push_enabled,
    };
  }

  return notificationSettingsValuesSchema.cast(value);
}

export function mapValuesToPayload(
  payload: NotificationSettingsValuesDTO,
): NotificationsSettingPayloadDTO[] {
  return Object.keys(payload)
    .map((activity: NotificationsSettingActivity) => {
      const value = payload[activity];
      return value ? { activity, ...value } : undefined;
    })
    .filter(Boolean)
    .map((x) => notificationsSettingPayloadSchema.cast(x));
}

export function isSavedSearchDisabled(
  {
    sms_enabled,
    email_enabled,
    push_enabled,
  }: NotificationSettingsValuesDTO['saved_search'],
  phoneState: UserSettingsPhoneState,
) {
  return phoneState === 'valid'
    ? !sms_enabled && !email_enabled && !push_enabled
    : !email_enabled && !push_enabled;
}
